/**
 * The receive-side reconcile (FEAT-040 wave 3) — the store and the server
 * agree within a foreground.
 *
 * Three buckets, judged against the LIVE unread list (C1 made it no-store,
 * which is a prerequisite, not a nicety: bucket C shreds on ABSENCE from
 * this list, and a stale service-worker copy would shred bytes for a
 * genuinely-unread moment — after C1, a flaky link surfaces as an error and
 * the pass aborts instead):
 *
 *   B. server-unread ∩ viewedLedger — this device viewed it OFFLINE. Replay
 *      the view-mark (POST first: if it fails the row stays for next pass —
 *      shredding first would let bucket A re-download a watched moment),
 *      then shred.
 *   A. server-unread, unviewed, unknown — enqueue for download.
 *   C. stored here, absent from server-unread — viewed elsewhere, deleted,
 *      or expired. Shred.
 *
 * Shaped on sendReconcile: the 20s window is spent only by a pass that
 * COMPLETES (BUG-097 — an offline pass must not lock out the next trigger),
 * concurrent callers coalesce, and nothing here ever throws at the caller.
 * Local expiry (envelope.expires_at in the past) runs before any network and
 * therefore even offline. Recovery boots skip the whole thing.
 */
import { api } from '../../api/client';
import type { ConversationPreview, Message } from '../../types';
import { inRecoveryMode } from '../../utils/bootGuard';
import { viewedLedger } from '../viewedLedger';
import { receiveEngine } from './receiveEngine';
import { receiveStore } from './receiveStore';

/** Friends judged per pass — the rest wait for the next trigger. Judge only
 * whole conversations actually asked about (the sendReconcile truncation
 * lesson: a silent cut that always lands in the same place never heals). */
const MAX_FRIENDS_PER_PASS = 5;
const MIN_INTERVAL_MS = 20_000;

let lastRun = 0;
let inFlight: Promise<void> | null = null;

export function reconcileInbox(
  userId: number,
  opts?: { force?: boolean }
): Promise<void> {
  if (inFlight) return inFlight;
  if (inRecoveryMode()) return Promise.resolve();
  const now = Date.now();
  if (!opts?.force && now - lastRun < MIN_INTERVAL_MS) return Promise.resolve();
  inFlight = run(userId).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function run(userId: number): Promise<void> {
  try {
    await receiveStore.whenReady();

    // Local, zero-network: a retention clock that ran out unopened. Even
    // offline the promise holds.
    for (const row of receiveStore.all()) {
      if (row.userId !== userId) continue;
      const expires = row.envelope.expires_at;
      if (expires && Date.parse(expires) < Date.now()) {
        await receiveStore.shred(row.messageId);
      }
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    const inbox = await api.get<ConversationPreview[]>('/messages/inbox');
    if (!inbox.success || !inbox.data) return; // no answer, no judgement

    const unreadCounts = new Map(
      inbox.data.map((c) => [c.friend_id, c.unread_count])
    );

    // Friends worth a list fetch: holding rows here, or showing unread
    // moments there. Held-rows friends go first — they are the ones a wrong
    // answer could hurt.
    const holding = new Set(
      receiveStore
        .all()
        .filter((row) => row.userId === userId)
        .map((row) => row.friendId)
    );
    const interesting: number[] = [];
    for (const friendId of holding) {
      if ((unreadCounts.get(friendId) ?? 0) === 0) {
        // The server says nothing of theirs is unread — every held row is
        // viewed/deleted/expired. No list fetch needed.
        for (const row of receiveStore.all()) {
          if (row.userId === userId && row.friendId === friendId) {
            await receiveStore.shred(row.messageId);
          }
        }
      } else {
        interesting.push(friendId);
      }
    }
    for (const convo of inbox.data) {
      if (
        convo.unread_count > 0 &&
        convo.last_message?.message_type !== 'text' &&
        !interesting.includes(convo.friend_id)
      ) {
        interesting.push(convo.friend_id);
      }
    }

    let enqueued = false;
    for (const friendId of interesting.slice(0, MAX_FRIENDS_PER_PASS)) {
      const list = await api.get<Message[]>(
        `/messages/conversation/${friendId}/unread`
      );
      if (!list.success || !list.data) return; // flaky answer — abort, retry later

      const serverUnread = new Set(list.data.map((m) => m.id));

      // Bucket B before A: a moment watched offline must never re-download.
      for (const message of list.data) {
        if (!viewedLedger.hasViewed(message.id)) continue;
        const marked = await api.post<Message>(
          `/messages/${message.id}/view-mark`
        );
        if (marked.success) await receiveStore.shred(message.id);
        // A failed mark leaves the row; the next pass tries again.
      }

      // Bucket A: genuinely unread and not yet ours.
      for (const message of list.data) {
        if (viewedLedger.hasViewed(message.id)) continue;
        if (receiveStore.has(message.id)) continue;
        if (message.message_type === 'text' || !message.media_url) continue;
        if (await receiveStore.enqueue(userId, message)) enqueued = true;
      }

      // Bucket C: held here, gone there.
      for (const row of receiveStore.all()) {
        if (row.userId !== userId || row.friendId !== friendId) continue;
        if (!serverUnread.has(row.messageId)) {
          await receiveStore.shred(row.messageId);
        }
      }
    }

    if (enqueued) receiveEngine.kick();

    // Only a pass that finished its judgement spends the window (BUG-097).
    lastRun = Date.now();
  } catch (err) {
    console.warn('[InboxReconcile] skipped:', err);
  }
}

/** Tests only. */
export function resetInboxReconcile(): void {
  lastRun = 0;
  inFlight = null;
}
